import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { IoCheckmarkCircleOutline, IoInformationCircleOutline, IoWarningOutline, IoLeafOutline } from 'react-icons/io5';
import { BASE_ROLE_LABELS } from '../../../utils/constants/packagingRoles';

const MAIN_STEPS = ['q0', 'q1', 'q2', 'q_imp_check'];
const MAIN_STEP_LABELS = [
    'Vlastný obal',
    'Vlastná výroba',
    'Zákazková výroba',
    'Dovoz / distribúcia',
];

// Výsledok diagnostiky je OBJEKT s dvoma nezávislými poľami {baseRole, producerRole}, nie jeden
// plochý enum - vyplýva to z čl. 3 ods. 1 bod 15 PPWR ("výrobcom je každý zhotoviteľ, dovozca
// alebo distribútor"), takže sa rola výrobcu KOMBINUJE so základnou rolou, nenahrádza ju.
//
// Cieľ prechodu v NODES môže byť:
//   - string (id ďalšieho uzla) - jednoduchý prechod
//   - { id, baseRole } - prechod, ktorý si navyše priebežne zapamätá zistenú baseRole
//     (číta sa neskôr, keď diagnostika v ČASTI 5 skončí producerRole výsledkom)
//   - { terminal: true, baseRole?, producerRole, producerReason? } - koniec diagnostiky;
//     ak baseRole chýba, použije sa hodnota už nastavená skôr (state)
const NODES = {
    // ═══ ČASŤ 1 — Vlastná výroba/plnenie ═══
    q0: {
        phase: 'main',
        question: 'Napĺňate prázdny obal vlastným produktom (vytvárate balený výrobok)?',
        hint: 'Napr. plníte fľaše, zabaľujete potraviny, plníte nádoby — aj keď obal samotný ste nevyrobili.',
        yes: 'q0b',
        no: 'q1',
    },
    q0b: {
        phase: 'sub',
        subgroup: 'brand-fill',
        subLabel: 'Kontrola značky obalu',
        subIndex: 1,
        subTotal: 1,
        question: 'Nesie tento balený výrobok/obal meno alebo ochrannú známku VÁŠHO ZÁKAZNÍKA (nie vašu vlastnú)?',
        hint: '',
        yes: { terminal: true, baseRole: 'MANUFACTURER_CUSTOMER', producerRole: 'NOT_APPLICABLE' },
        no: { id: 'q_prod_export', baseRole: 'MANUFACTURER_SELF' },
    },

    // ═══ ČASŤ 2 — Fyzická výroba obalu (s kontrolou značky) ═══
    q1: {
        phase: 'main',
        question: 'Fyzicky vyrábate/zhotovujete obal vy sami vo vlastnej prevádzke?',
        hint: 'Vzťahuje sa na priamu výrobu obalu vo vlastných prevádzkach.',
        yes: 'q1b',
        no: 'q2',
    },
    q1b: {
        phase: 'sub',
        subgroup: 'brand-manufacture',
        subLabel: 'Kontrola značky a dizajnu obalu',
        subIndex: 1,
        subTotal: 2,
        question: 'Nesie tento obal meno alebo ochrannú známku VÁŠHO ZÁKAZNÍKA (nie vašu vlastnú)?',
        hint: '',
        yes: { terminal: true, baseRole: 'MANUFACTURER_CUSTOMER', producerRole: 'NOT_APPLICABLE' },
        no: 'q1c',
    },
    q1c: {
        phase: 'sub',
        subgroup: 'brand-manufacture',
        subLabel: 'Kontrola značky a dizajnu obalu',
        subIndex: 2,
        subTotal: 2,
        question: 'Vy sami rozhodujete o špecifikáciách dizajnu tohto obalu (nie je to len objednávka podľa parametrov zadaných zákazníkom)?',
        hint: '',
        yes: { id: 'q_prod_export', baseRole: 'MANUFACTURER_SELF' },
        no: { terminal: true, baseRole: 'MANUFACTURER_CUSTOMER', producerRole: 'NOT_APPLICABLE' },
    },

    // ═══ ČASŤ 3 — Zadanie výroby tretej strane pod vlastnou značkou ═══
    q2: {
        phase: 'main',
        question: 'Dávate si obal alebo balený výrobok navrhnúť alebo zhotoviť treťou stranou pod vaším vlastným menom alebo ochrannou známkou?',
        hint: 'Platí bez ohľadu na to, či je na obale viditeľná aj iná ochranná známka.',
        yes: 'q3',
        no: 'q_imp_check',
    },
    q3: {
        phase: 'sub',
        subgroup: 'micro',
        subLabel: 'Výnimka pre mikropodniky',
        subIndex: 1,
        subTotal: 2,
        question: 'Ste mikropodnik (menej ako 10 zamestnancov A ročný obrat/bilancia do 2 mil. EUR)?',
        hint: 'Podľa odporúčania 2003/361/ES.',
        yes: 'q4',
        no: { id: 'q_prod_export', baseRole: 'MANUFACTURER_SELF' },
    },
    q4: {
        phase: 'sub',
        subgroup: 'micro',
        subLabel: 'Výnimka pre mikropodniky',
        subIndex: 2,
        subTotal: 2,
        question: 'Sídli dodávateľ, ktorý vám obal dodáva, v tom istom členskom štáte ako vy?',
        hint: '',
        yes: { terminal: true, baseRole: 'MANUFACTURER_SUPPLIER', producerRole: 'NOT_APPLICABLE' },
        no: { id: 'q_prod_export', baseRole: 'MANUFACTURER_SELF' },
    },

    // ═══ ČASŤ 4A — Kontrola dovozca/distribútor ═══
    q_imp_check: {
        phase: 'main',
        type: 'info',
        question: 'Vstupujete do tejto transakcie ako dovozca (tovar z tretej krajiny) alebo ako distribútor (ďalej predávate obal v rámci EÚ)?',
        hint: 'V ďalšom kroku upresníte, o ktorú z týchto dvoch kategórií ide.',
        next: 'q_imp_or_dist',
    },
    q_imp_or_dist: {
        phase: 'sub',
        subgroup: 'importer',
        subLabel: 'Postavenie dovozcu / distribútora',
        subIndex: 1,
        subTotal: 3,
        type: 'choice',
        question: 'V akom postavení uvádzate obal na trh?',
        hint: '',
        options: [
            { label: 'Som dovozca (obal z tretej krajiny)', value: 'IMPORTER', next: { id: 'q5', baseRole: 'IMPORTER' } },
            { label: 'Som distribútor (obal v rámci EÚ)', value: 'DISTRIBUTOR', next: { id: 'q5', baseRole: 'DISTRIBUTOR' } },
        ],
    },

    // ═══ ČASŤ 4B — Overenie, či dovozca/distribútor nespĺňa znaky zhotoviteľa (čl. 21) ═══
    q5: {
        phase: 'sub',
        subgroup: 'importer',
        subLabel: 'Postavenie dovozcu / distribútora',
        subIndex: 2,
        subTotal: 3,
        question: 'Uvádzate obal pod vlastným menom/ochrannou známkou?',
        hint: '',
        yes: 'q3_imp',
        no: 'q6',
    },
    q6: {
        phase: 'sub',
        subgroup: 'importer',
        subLabel: 'Postavenie dovozcu / distribútora',
        subIndex: 3,
        subTotal: 3,
        question: 'Upravili ste obal spôsobom, ktorý by mohol ovplyvniť jeho súlad s nariadením?',
        hint: 'Napr. zmena materiálu, dizajnu alebo štruktúry obalu.',
        yes: 'q3_imp',
        no: { id: 'q_prod_export' },
    },
    q3_imp: {
        phase: 'sub',
        subgroup: 'micro',
        subLabel: 'Výnimka pre mikropodniky',
        subIndex: 1,
        subTotal: 2,
        question: 'Ste mikropodnik (menej ako 10 zamestnancov A ročný obrat/bilancia do 2 mil. EUR)?',
        hint: 'Podľa odporúčania 2003/361/ES. Testuje sa mikropodnikový status vás (dovozcu/distribútora), nie dodávateľa.',
        yes: 'q4_imp',
        no: { id: 'q_prod_export', baseRole: 'MANUFACTURER_SELF' },
    },
    q4_imp: {
        phase: 'sub',
        subgroup: 'micro',
        subLabel: 'Výnimka pre mikropodniky',
        subIndex: 2,
        subTotal: 2,
        question: 'Sídli dodávateľ obalu (od ktorého ste obal nadobudli) v tom istom členskom štáte ako vy?',
        hint: '',
        yes: { terminal: true, baseRole: 'MANUFACTURER_SUPPLIER', producerRole: 'NOT_APPLICABLE' },
        no: { id: 'q_prod_export', baseRole: 'MANUFACTURER_SELF' },
    },

    // ═══ ČASŤ 5 — Test roly Výrobca (čl. 3 ods. 1 bod 15). Spúšťa sa VŽDY, keď baseRole je
    // MANUFACTURER_SELF, IMPORTER alebo DISTRIBUTOR - výsledok sa s ňou KOMBINUJE. ═══
    q_prod_export: {
        phase: 'sub',
        subgroup: 'producer',
        subLabel: 'Povinnosti výrobcu',
        subIndex: 1,
        subTotal: 5,
        question: 'Obal alebo balený výrobok sa vyváža mimo EÚ, kde sa očakáva, že sa stane odpadom?',
        hint: '',
        yes: { terminal: true, producerRole: 'PRODUCER_EXEMPT_EXPORT' },
        no: 'q_prod_master',
    },
    q_prod_master: {
        phase: 'sub',
        subgroup: 'producer',
        subLabel: 'Povinnosti výrobcu',
        subIndex: 2,
        subTotal: 5,
        question: 'Máte sídlo (ste usadený) v členskom štáte, v ktorom sa obal pravdepodobne stane odpadom (t. j. kde ho po prvýkrát sprístupňujete z toho istého územia, z ktorého ste usadení)?',
        hint: '',
        yes: { terminal: true, producerRole: 'PRODUCER', producerReason: 'AB' },
        no: 'q_prod_other_company',
    },
    q_prod_other_company: {
        phase: 'sub',
        subgroup: 'producer',
        subLabel: 'Povinnosti výrobcu',
        subIndex: 3,
        subTotal: 5,
        question: 'Existuje v členskom štáte, kde sa obal stane odpadom, INÁ spoločnosť, ktorá ho tam sprístupňuje po prvýkrát (napr. distribútor, dovozca, alebo ten, kto tam obal vybaľuje)?',
        hint: '',
        yes: { terminal: true, producerRole: 'PRODUCER_OTHER' },
        no: 'q_prod_direct_enduser',
    },
    q_prod_direct_enduser: {
        phase: 'sub',
        subgroup: 'producer',
        subLabel: 'Povinnosti výrobcu',
        subIndex: 4,
        subTotal: 5,
        question: 'Dodali ste obal/balený výrobok PRIAMO koncovému používateľovi (nie ďalšiemu B2B článku ako je distribútor/supermarket) v inom členskom štáte?',
        hint: '',
        yes: { terminal: true, producerRole: 'PRODUCER', producerReason: 'CD' },
        no: 'q_prod_unpack',
    },
    q_prod_unpack: {
        phase: 'sub',
        subgroup: 'producer',
        subLabel: 'Povinnosti výrobcu',
        subIndex: 5,
        subTotal: 5,
        question: 'Vybaľujete alebo prebaľujete balené výrobky, pričom NIE STE koncovým používateľom (t. j. výrobok/obal posúvate ďalej)?',
        hint: '',
        yes: { terminal: true, producerRole: 'PRODUCER', producerReason: 'E' },
        no: { terminal: true, producerRole: 'NOT_PRODUCER' },
    },
};

const BASE_ROLE_INFO = {
    MANUFACTURER_SELF: {
        Icon: IoCheckmarkCircleOutline,
        color: '#198754',
        description: 'Vy sami vyrábate/plníte obal, alebo si ho necháte zhotoviť treťou stranou pod vlastným menom či ochrannou známkou (čl. 3 bod 13 PPWR).',
    },
    MANUFACTURER_SUPPLIER: {
        Icon: IoInformationCircleOutline,
        color: '#0d6efd',
        description: 'Ako mikropodnik s dodávateľom z rovnakého členského štátu sa zodpovednosť zhotoviteľa prenáša na vášho dodávateľa obalu.',
    },
    MANUFACTURER_CUSTOMER: {
        Icon: IoInformationCircleOutline,
        color: '#0d6efd',
        description: 'Obal alebo balený výrobok nesie meno alebo ochrannú známku vášho zákazníka, prípadne zákazník sám rozhoduje o jeho dizajne/špecifikáciách — zodpovednosť zhotoviteľa podľa čl. 3 bod 13 PPWR preto nesie on, nie vy. V tomto vzťahu vystupujete ako jeho dodávateľ.',
    },
    IMPORTER: {
        Icon: IoWarningOutline,
        color: '#fd7e14',
        description: 'Uvádzate na trh EÚ obal zhotovený mimo vašej zodpovednosti, dovezený z tretej krajiny (čl. 18 PPWR).',
    },
    DISTRIBUTOR: {
        Icon: IoInformationCircleOutline,
        color: '#6f42c1',
        description: 'Sprístupňujete na trhu obal, ktorý zhotoviteľ alebo dovozca už uviedol na trh EÚ (čl. 19 PPWR).',
    },
};

const PRODUCER_REASON_TEXT = {
    AB: 'Ste usadený v členskom štáte, v ktorom obal alebo balený výrobok po prvýkrát sprístupňujete z toho istého územia — ide buď o prázdny prepravný/servisný/prvovýrobný obal (písm. a), alebo o balené výrobky v inom type obalu, napr. spotrebiteľskom či skupinovom (písm. b).',
    CD: 'Dodávate prázdny obal (písm. c) alebo balený výrobok (písm. d) po prvýkrát priamo koncovému používateľovi v inom členskom štáte, než v ktorom ste usadení — napr. cezhraničný predaj cez e-shop.',
    E: 'Vybaľujete alebo prebaľujete balené výrobky bez toho, aby ste boli ich koncovým používateľom — takýto subjekt sa nepovažuje za koncového používateľa a automaticky sa stáva výrobcom obalu, keďže túto úlohu v reťazci neplní nikto iný.',
};

// Text vysvetľujúci producerRole - platí NEZÁVISLE od baseRole (viď implementačná poznámka #6:
// text má vysvetliť OBE roly samostatne, nie len skratku).
const PRODUCER_ROLE_TEXT = {
    PRODUCER: (reason) => `Ste zároveň aj výrobcom podľa čl. 3 ods. 1 bod 15 PPWR. ${PRODUCER_REASON_TEXT[reason] ?? PRODUCER_REASON_TEXT.AB} Popri povinnostiach vyplývajúcich z vášho základného postavenia preto navyše riešite aj registráciu výrobcu (čl. 44) a plnú dokumentáciu zhotoviteľa (posúdenie zhody, EÚ vyhlásenie, technická dokumentácia).`,
    PRODUCER_OTHER: () => 'Výrobcom podľa čl. 3 ods. 1 bod 15 PPWR však nie ste vy — v členskom štáte, kde sa tento obal stane odpadom, ho po prvýkrát sprístupňuje iná spoločnosť (napr. distribútor, dovozca, alebo ten, kto ho tam vybaľuje).',
    PRODUCER_EXEMPT_EXPORT: () => 'Výrobca podľa čl. 3 ods. 1 bod 15 PPWR vo vašom prípade v žiadnom členskom štáte nevzniká — obal sa vyváža mimo EÚ do tretej krajiny, kde sa očakáva, že sa stane odpadom.',
    NOT_PRODUCER: () => 'Postavenie výrobcu podľa čl. 3 ods. 1 bod 15 PPWR vám pre tento konkrétny obal nevzniká v žiadnom členskom štáte (napr. ide o B2B dodávku do iného členského štátu, v ktorom nie ste usadení a nedodávate priamo koncovému používateľovi).',
};

const PRODUCER_TITLE_SUFFIX = {
    PRODUCER: (base) => `${base} + Výrobca`,
    PRODUCER_OTHER: (base) => `${base} (výrobca je iný subjekt)`,
    PRODUCER_EXEMPT_EXPORT: (base) => `${base} (vývoz mimo EÚ, bez EPR)`,
    NOT_PRODUCER: (base) => `${base} (bez EPR povinností v tomto ČŠ)`,
};

// Zostaví obsah výslednej obrazovky z {baseRole, producerRole, producerReason} - titulok podľa
// kombinačnej matice, a DVE samostatné vysvetlenia (base + producer), keď sú obe relevantné.
const buildResultDisplay = (result) => {
    const { baseRole, producerRole, producerReason } = result;
    const baseLabel = BASE_ROLE_LABELS[baseRole] ?? baseRole;
    const baseInfo = BASE_ROLE_INFO[baseRole] ?? BASE_ROLE_INFO.MANUFACTURER_SELF;
    const isProducerApplicable = producerRole && producerRole !== 'NOT_APPLICABLE';

    const title = isProducerApplicable ? (PRODUCER_TITLE_SUFFIX[producerRole]?.(baseLabel) ?? baseLabel) : baseLabel;
    const Icon = producerRole === 'PRODUCER' ? IoLeafOutline : baseInfo.Icon;
    const color = producerRole === 'PRODUCER' ? '#0f5132' : baseInfo.color;

    const descriptions = [baseInfo.description];
    if (isProducerApplicable) {
        descriptions.push(PRODUCER_ROLE_TEXT[producerRole]?.(producerReason) ?? '');
    }

    return {
        Icon,
        color,
        title,
        subtitle: isProducerApplicable ? 'Kombinované postavenie' : 'Vaše postavenie voči tomuto obalu',
        descriptions,
    };
};

const MainProgress = ({ currentStep, completed }) => (
    <div className="mb-4">
        <div className="d-flex gap-2">
            {MAIN_STEPS.map((_, i) => {
                const step = i + 1;
                const isDone = completed || step < currentStep;
                const isCurrent = !completed && step === currentStep;
                return (
                    <div
                        key={step}
                        style={{
                            flex: 1,
                            height: 6,
                            borderRadius: 4,
                            backgroundColor: isDone ? '#198754' : isCurrent ? '#86b7fe' : '#e9ecef',
                            transition: 'background-color 0.2s ease',
                        }}
                    />
                );
            })}
        </div>
        <p className="text-muted small mb-0 mt-2">
            {completed
                ? 'Hlavná diagnostika dokončená'
                : `Otázka ${currentStep} z ${MAIN_STEPS.length} · ${MAIN_STEP_LABELS[currentStep - 1]}`}
        </p>
    </div>
);

const SubProgress = ({ label, index, total }) => (
    <div
        className="mb-4 d-flex align-items-center gap-2 px-3 py-2"
        style={{ backgroundColor: '#f0f6ff', border: '1px solid #cfe2ff', borderRadius: 8 }}
    >
        <div className="d-flex gap-1">
            {Array.from({ length: total }).map((_, i) => (
                <div
                    key={i}
                    style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: i < index ? '#0d6efd' : '#b6d4fe',
                    }}
                />
            ))}
        </div>
        <span className="small text-primary fw-semibold">
            Doplňujúca otázka · {label} ({index}/{total})
        </span>
    </div>
);

const resolveTarget = (raw) => (typeof raw === 'string' ? { id: raw } : raw);

const RoleWizard = ({ onComplete, onClose }) => {
    const [history, setHistory] = useState([]);
    const [currentId, setCurrentId] = useState('q0');
    const [baseRole, setBaseRole] = useState(null);
    const [result, setResult] = useState(null);

    // Ukončí diagnostiku - baseRole v descriptore (ak je) má prednosť, inak sa použije hodnota
    // nastavená na predchádzajúcom kroku (state), keďže sa nemení v rovnakom kliknutí. currentId
    // aj history sa nemenia - "Späť" z výsledku sa tak vráti presne na naposledy zodpovedanú otázku.
    const finish = (descriptor) => {
        setResult({
            baseRole: descriptor.baseRole ?? baseRole,
            producerRole: descriptor.producerRole ?? 'NOT_APPLICABLE',
            producerReason: descriptor.producerReason,
        });
    };

    const applyTarget = (raw) => {
        const target = resolveTarget(raw);
        if (target.baseRole !== undefined) setBaseRole(target.baseRole);
        return target.id;
    };

    const handleAnswer = (answer) => {
        const node = NODES[currentId];
        const raw = node[answer];
        if (raw?.terminal) { finish(raw); return; }
        const next = applyTarget(raw);
        setHistory(h => [...h, currentId]);
        setCurrentId(next);
    };

    const handleChoice = (option) => {
        if (option.next?.terminal) { finish(option.next); return; }
        const next = applyTarget(option.next);
        setHistory(h => [...h, currentId]);
        setCurrentId(next);
    };

    const handleInfo = (next) => {
        if (next?.terminal) { finish(next); return; }
        const id = applyTarget(next);
        setHistory(h => [...h, currentId]);
        setCurrentId(id);
    };

    const handleBack = () => {
        if (result) { setResult(null); return; }
        const prev = history[history.length - 1];
        setHistory(h => h.slice(0, -1));
        setCurrentId(prev);
    };

    // Skratka na q0: rovno baseRole=MANUFACTURER_SELF, ale diagnostika pokračuje ČASŤOU 5
    // (test výrobcu) - nekončí sa okamžite, presne ako pri ostatných vetvách vedúcich k nej.
    const handleSkipToManufacturer = () => {
        const next = applyTarget({ id: 'q_prod_export', baseRole: 'MANUFACTURER_SELF' });
        setHistory(h => [...h, 'q0']);
        setCurrentId(next);
    };

    if (result) {
        const display = buildResultDisplay(result);
        const { Icon } = display;
        return (
            <div>
                <h2>Diagnostika postavenia</h2>
                <MainProgress completed />
                <div className="text-center py-4">
                    <Icon size={72} style={{ color: display.color }} />
                    <h3 className="mt-3 mb-1">{display.title}</h3>
                    <p className="text-muted mb-3">{display.subtitle}</p>
                    {(display.descriptions ?? [display.description]).filter(Boolean).map((text, i) => (
                        <p key={i} className="mx-auto" style={{ maxWidth: 480 }}>{text}</p>
                    ))}
                </div>
                <hr />
                <div className="d-flex justify-content-between align-items-center">
                    <Button variant="link" className="text-muted ps-0" onClick={handleBack}>← Späť</Button>
                    <Button
                        className="button-custom-large"
                        variant="primary"
                        size="lg"
                        onClick={() => onComplete({ baseRole: result.baseRole, producerRole: result.producerRole })}
                    >
                        Pokračovať k pridaniu obalovej jednotky
                    </Button>
                </div>
            </div>
        );
    }

    const node = NODES[currentId];

    return (
        <div>
            <h2>Diagnostika postavenia</h2>

            {node.phase === 'main' ? (
                <MainProgress currentStep={MAIN_STEPS.indexOf(currentId) + 1} />
            ) : (
                <SubProgress label={node.subLabel} index={node.subIndex} total={node.subTotal} />
            )}

            <h4 className="mb-3" style={{ lineHeight: 1.4 }}>{node.question}</h4>
            {node.hint && (
                <p className="text-muted small mb-4">{node.hint}</p>
            )}

            <div className="d-flex gap-3 justify-content-center my-5">
                {node.type === 'info' ? (
                    <Button
                        variant="primary"
                        size="lg"
                        style={{ minWidth: 200 }}
                        onClick={() => handleInfo(node.next)}
                    >
                        Rozumiem, pokračovať →
                    </Button>
                ) : node.type === 'choice' ? (
                    node.options.map(option => (
                        <Button
                            key={option.value}
                            variant="outline-primary"
                            size="lg"
                            style={{ minWidth: 160 }}
                            onClick={() => handleChoice(option)}
                        >
                            {option.label}
                        </Button>
                    ))
                ) : (
                <>
                <Button
                    variant="outline-secondary"
                    size="lg"
                    style={{ minWidth: 120 }}
                    onClick={() => handleAnswer('no')}
                >
                    NIE
                </Button>
                <Button
                    variant="success"
                    size="lg"
                    style={{ minWidth: 120 }}
                    onClick={() => handleAnswer('yes')}
                >
                    ÁNO
                </Button>
                </>
                )}
            </div>

            {currentId === 'q0' && (
                <div className="text-center mb-4">
                    <Button
                        variant="link"
                        className="text-muted"
                        onClick={handleSkipToManufacturer}
                    >
                        Viem, že som zhotoviteľ — preskočiť diagnostiku
                    </Button>
                </div>
            )}

            <hr />
            <div>
                {history.length > 0 ? (
                    <Button variant="link" className="text-muted ps-0" onClick={handleBack}>← Späť</Button>
                ) : (
                    <Button variant="link" className="text-muted ps-0" onClick={onClose}>Zrušiť</Button>
                )}
            </div>
        </div>
    );
};

export default RoleWizard;
